import React, { useState } from "react";
import { Plus, RotateCw, Trash2 } from "lucide-react";
import { PATTERNS, GROUTS, walls } from "./cadGeometry";
import { swatchUrl } from "./tileTexture";

export const INPUT = "bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream focus:outline-none focus:border-rossi-gold/50 w-full";
const LBL = "text-[11px] font-mono uppercase tracking-widest text-rossi-stone mb-1.5 block";
const chip = (on) => `px-2.5 py-1.5 rounded-full text-xs transition-colors ${on ? "bg-rossi-gold text-rossi-bg" : "border border-white/10 text-rossi-cream/75 hover:border-rossi-gold/40"}`;

const NumField = ({ label, value, onChange, step = 0.05, testid }) => (
  <label className="block"><span className={LBL}>{label}</span>
    <input type="number" step={step} value={value} onChange={(e) => onChange(parseFloat(e.target.value))} className={INPUT} data-testid={testid} />
  </label>
);

export const RoomPanel = ({ state, update }) => {
  const r = state.room;
  const set = (k, v) => { if (isFinite(v)) update({ op: "set_room", [k]: v }); };
  return (
    <div className="space-y-4" data-testid="cad-room-panel">
      <div><span className={LBL}>Room shape</span>
        <div className="flex gap-2">{[["rect", "Rectangle"], ["L", "L-shape"]].map(([k, l]) => <button key={k} onClick={() => update({ op: "set_room", shape: k })} className={chip(r.shape === k)} data-testid={`room-shape-${k}`}>{l}</button>)}</div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <NumField label="Length m" value={r.length} onChange={(v) => set("length", v)} testid="room-length" />
        <NumField label="Width m" value={r.width} onChange={(v) => set("width", v)} testid="room-width" />
        <NumField label="Height m" value={r.height} onChange={(v) => set("height", v)} testid="room-height" />
      </div>
      {r.shape === "L" && (
        <div className="grid grid-cols-2 gap-2">
          <NumField label="Notch length m" value={r.cut_length} onChange={(v) => set("cut_length", v)} testid="room-cut-length" />
          <NumField label="Notch depth m" value={r.cut_width} onChange={(v) => set("cut_width", v)} testid="room-cut-width" />
        </div>
      )}
      <label className="block"><span className={LBL}>Wall paint (untiled areas)</span>
        <input type="color" value={state.paint} onChange={(e) => update({ op: "set_paint", colour: e.target.value })} className="h-10 w-20 rounded bg-transparent" data-testid="room-paint" />
      </label>
    </div>
  );
};

export const SurfacePanel = ({ state, collections, sel, setSel, update }) => {
  const sp = state.surfaces[sel] || {};
  const col = collections.find((c) => c.id === sp.tile_id);
  const set = (patch) => update({ op: "set_surface", target: sel, ...patch });
  const opts = [{ key: "floor", label: "Floor" }, ...walls(state.room)];
  const tiles = collections.filter((c) => !c.panel);
  const panels = sel === "floor" ? [] : collections.filter((c) => c.panel);
  const Sw = ({ c }) => (
    <button onClick={() => set({ tile_id: c.id, tile_w: c.tile_w, tile_h: c.tile_h })} title={c.name} className={`aspect-square rounded-lg overflow-hidden border-2 ${sp.tile_id === c.id ? "border-rossi-gold" : "border-transparent hover:border-white/30"}`} data-testid={`tile-${c.id}`}>
      {c.swatch ? <img src={swatchUrl(c.swatch)} alt={c.name} className="w-full h-full object-cover" /> : <span className="block w-full h-full" style={{ background: c.colour }} />}
    </button>
  );
  return (
    <div className="space-y-4" data-testid="cad-surface-panel">
      <div><span className={LBL}>Surface</span>
        <div className="flex flex-wrap gap-1.5">{opts.map((o) => <button key={o.key} onClick={() => setSel(o.key)} className={chip(sel === o.key)} data-testid={`surface-${o.key}`}>{o.label}</button>)}</div>
      </div>
      <div><span className={LBL}>Tile</span>
        <div className="grid grid-cols-4 gap-2 max-h-48 overflow-auto pr-1">
          {sel !== "floor" && <button onClick={() => set({ tile_id: null })} className={`aspect-square rounded-lg border text-[10px] text-rossi-cream/70 ${!sp.tile_id ? "border-rossi-gold" : "border-white/10"}`} style={{ background: state.paint + "33" }} data-testid="tile-none">Paint</button>}
          {tiles.map((c) => <Sw key={c.id} c={c} />)}
        </div>
        {panels.length > 0 && <><span className={`${LBL} mt-3`}>Splashback panels</span><div className="grid grid-cols-4 gap-2">{panels.map((c) => <Sw key={c.id} c={c} />)}</div></>}
        {col && <p className="text-xs text-rossi-cream mt-2">{col.name} <span className="text-rossi-stone">· {col.material}</span></p>}
      </div>
      {col && (
        <>
          {col.swatch_mode === "pattern" ? <p className="text-xs text-rossi-stone">Joints are built into this finish — size &amp; grout follow the product.</p> : (
            <>
              <div><span className={LBL}>Pattern</span><div className="flex flex-wrap gap-1.5">{PATTERNS.map((p) => <button key={p.id} onClick={() => set({ pattern: p.id })} className={chip((sp.pattern || "grid") === p.id)} data-testid={`pattern-${p.id}`}>{p.label}</button>)}</div></div>
              <div className="grid grid-cols-3 gap-2">
                <NumField label="Tile W mm" step={10} value={sp.tile_w} onChange={(v) => isFinite(v) && v >= 20 && set({ tile_w: v })} testid="tile-w" />
                <NumField label="Tile H mm" step={10} value={sp.tile_h} onChange={(v) => isFinite(v) && v >= 20 && set({ tile_h: v })} testid="tile-h" />
                <NumField label="Grout mm" step={0.5} value={sp.grout_mm} onChange={(v) => isFinite(v) && v >= 0 && set({ grout_mm: v })} testid="grout-mm" />
              </div>
              <div><span className={LBL}>Grout colour</span><div className="flex gap-2">{GROUTS.map((g) => <button key={g} onClick={() => set({ grout_colour: g })} className={`h-7 w-7 rounded-full border-2 ${sp.grout_colour === g ? "border-rossi-gold" : "border-white/15"}`} style={{ background: g }} data-testid={`grout-${g.slice(1)}`} />)}</div></div>
            </>
          )}
          {sel !== "floor" && (
            <div><span className={LBL}>Tiled height</span>
              <div className="flex flex-wrap gap-1.5">{[[null, "Full height"], [1.2, "1.2 m"], [0.9, "0.9 m"], [0.6, "Splashback 0.6 m"]].map(([v, l]) => <button key={l} onClick={() => set({ tiled_height: v })} className={chip((sp.tiled_height || null) === v)} data-testid={`height-${v || "full"}`}>{l}</button>)}</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export const FixturePanel = ({ state, fixtures, meta, brassware, setBrass, selected, add, patch, remove }) => {
  const f = state.fixtures.find((x) => x.uid === selected);
  const cats = [...new Set(fixtures.map((m) => m.category))];
  const [cat, setCat] = useState(null);
  const active = cat || cats[0];
  return (
    <div className="space-y-4" data-testid="cad-fixture-panel">
      {f && meta[f.type] && (
        <div className="rounded-xl border border-rossi-gold/30 bg-rossi-gold/5 p-3" data-testid="selected-fixture">
          <p className="text-sm text-rossi-cream mb-2">{meta[f.type].name}</p>
          <div className="grid grid-cols-2 gap-2">
            <NumField label="X mm" step={10} value={Math.round(f.x * 1000)} onChange={(v) => isFinite(v) && patch(f.uid, { x: v / 1000 })} testid="fixture-x" />
            <NumField label="Z mm" step={10} value={Math.round(f.z * 1000)} onChange={(v) => isFinite(v) && patch(f.uid, { z: v / 1000 })} testid="fixture-z" />
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => patch(f.uid, { rot: ((f.rot + 90 + 180) % 360) - 180 })} className="btn-ghost-gold !py-1.5 !px-3 text-xs" data-testid="fixture-rotate"><RotateCw size={13} /> Rotate 90°</button>
            <button onClick={() => remove(f.uid)} className="btn-ghost-gold !py-1.5 !px-3 text-xs" data-testid="fixture-delete"><Trash2 size={13} /> Remove</button>
          </div>
        </div>
      )}
      <div><span className={LBL}>Brassware finish (taps, shower, trims)</span>
        <div className="flex flex-wrap gap-1.5">{brassware.map((b) => <button key={b.id} onClick={() => setBrass(b.id)} className={`${chip(state.brassware === b.id)} flex items-center gap-1.5`} data-testid={`brass-${b.id}`}><span className="h-3 w-3 rounded-full border border-white/30" style={{ background: b.colour }} />{b.name.replace(" mixer tap", "")}</button>)}</div>
      </div>
      <div><span className={LBL}>Add to room</span>
        <div className="flex flex-wrap gap-1.5 mb-3">{cats.map((c) => <button key={c} onClick={() => setCat(c)} className={chip(active === c)} data-testid={`fixture-cat-${c.replace(/\W+/g, "-").toLowerCase()}`}>{c}</button>)}</div>
        <div className="space-y-2 max-h-72 overflow-auto pr-1">
          {fixtures.filter((m) => m.category === active).map((m) => (
            <div key={m.id} className="flex items-center gap-3 border-b border-white/5 pb-2 last:border-0">
              <div className="flex-1 min-w-0"><p className="text-sm text-rossi-cream truncate" title={m.name}>{m.name}</p><p className="text-[11px] font-mono text-rossi-stone">{Math.round(m.w * 1000)}×{Math.round(m.d * 1000)} · <span className="text-rossi-gold">{m.price_note || `£${Math.round(m.price_gbp).toLocaleString("en-GB")}`}</span></p></div>
              <button onClick={() => add(m.id)} className="h-8 w-8 shrink-0 rounded-full border border-rossi-gold/40 text-rossi-gold hover:bg-rossi-gold/10 flex items-center justify-center" data-testid={`add-fixture-${m.id}`}><Plus size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
